import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { request } from './client'
import type {
  AvailabilityResponse,
  BlocklistEntry,
  BookingCreate,
  BookingCreated,
  BookingDetails,
  Device,
  FutureBooking,
  MeetingsResponse,
  MeResponse,
  PairingCodeResponse,
  ParticipantRef,
  ParticipantsResponse,
  Policy,
  RequestCodeResponse,
  Room,
  RoomWrite,
  ScheduleResponse,
  SlotsResponse,
  User,
  UserStatus,
  UserSuggestion,
  VerifyResponse,
} from './types'

/**
 * Ключи кеша собраны в одном месте: инвалидация после мутаций должна попадать точно,
 * иначе таймлайн показывает вчерашнюю картину, а человек думает, что бронь не создалась.
 */
export const keys = {
  me: ['me'] as const,
  rooms: (includeHidden = false) => ['rooms', includeHidden] as const,
  room: (id: number) => ['room', id] as const,
  schedule: (date: string) => ['schedule', date] as const,
  availability: (start: string, end: string, minCapacity?: number) =>
    ['availability', start, end, minCapacity ?? 0] as const,
  slots: (roomId: number, date: string) => ['slots', roomId, date] as const,
  bookings: (scope: string, role: string) => ['bookings', scope, role] as const,
  booking: (id: number) => ['booking', id] as const,
  userSearch: (q: string) => ['user-search', q] as const,
  adminUsers: (q: string, status?: string) => ['admin-users', q, status ?? ''] as const,
  devices: ['admin-devices'] as const,
  policies: ['admin-policies'] as const,
  blocklist: ['admin-blocklist'] as const,
  futureBookings: (roomId: number) => ['admin-future-bookings', roomId] as const,
}

/** Всё, что меняет расписание, обесценивает сразу несколько списков. */
function useScheduleInvalidation() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['schedule'] })
    void queryClient.invalidateQueries({ queryKey: ['slots'] })
    void queryClient.invalidateQueries({ queryKey: ['availability'] })
    void queryClient.invalidateQueries({ queryKey: ['bookings'] })
    void queryClient.invalidateQueries({ queryKey: ['booking'] })
  }
}

// ---------------------------------------------------------------------------
// Вход
// ---------------------------------------------------------------------------

export function useMe(options?: Partial<UseQueryOptions<MeResponse>>) {
  return useQuery<MeResponse>({
    queryKey: keys.me,
    queryFn: () => request<MeResponse>('/auth/me'),
    // Сессия могла закончиться где угодно; повторять запрос на 401 бессмысленно.
    retry: false,
    staleTime: 60_000,
    ...options,
  })
}

export function useRequestCode() {
  return useMutation({
    mutationFn: (email: string) =>
      request<RequestCodeResponse>('/auth/request-code', { method: 'POST', body: { email } }),
  })
}

export function useVerifyCode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; code: string }) =>
      request<VerifyResponse>('/auth/verify', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.me })
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => request<void>('/auth/logout', { method: 'POST' }),
    onSuccess: () => queryClient.clear(),
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { first_name?: string; last_name?: string; position?: string }) =>
      request<User>('/auth/me', { method: 'PATCH', body: input }),
    // Обещание возвращается, а не гасится через void: react-query дождётся его,
    // прежде чем позвать onSuccess у самого вызова. Иначе «Знакомство» уходило в петлю —
    // экран успевал сделать navigate('/') раньше, чем обновится профиль, RequireAuth
    // читал из кеша прежний needs_profile=true и возвращал человека на пустую форму.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.me }),
  })
}

// ---------------------------------------------------------------------------
// Расписание
// ---------------------------------------------------------------------------

export function useRooms(includeHidden = false) {
  return useQuery<Room[]>({
    queryKey: keys.rooms(includeHidden),
    queryFn: () => request<Room[]>('/rooms', { query: { include_hidden: includeHidden } }),
    staleTime: 5 * 60_000,
  })
}

export function useRoom(roomId: number | undefined) {
  return useQuery<Room>({
    queryKey: keys.room(roomId ?? 0),
    queryFn: () => request<Room>(`/rooms/${roomId}`),
    enabled: roomId !== undefined && Number.isFinite(roomId),
  })
}

export function useSchedule(date: string) {
  return useQuery<ScheduleResponse>({
    queryKey: keys.schedule(date),
    queryFn: () => request<ScheduleResponse>('/schedule', { query: { date } }),
    // Расписание общее: пока человек смотрит на него, кто-то мог занять комнату.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function useAvailability(startsAt: string, endsAt: string, minCapacity?: number, enabled = true) {
  return useQuery<AvailabilityResponse>({
    queryKey: keys.availability(startsAt, endsAt, minCapacity),
    queryFn: () =>
      request<AvailabilityResponse>('/availability', {
        query: { starts_at: startsAt, ends_at: endsAt, min_capacity: minCapacity },
      }),
    enabled: enabled && Boolean(startsAt && endsAt),
    // Список под формой пересчитывается сразу, без кнопки «Найти», — но и без мигания.
    placeholderData: (previous) => previous,
  })
}

export function useSlots(roomId: number | undefined, date: string) {
  return useQuery<SlotsResponse>({
    queryKey: keys.slots(roomId ?? 0, date),
    queryFn: () => request<SlotsResponse>(`/rooms/${roomId}/slots`, { query: { date } }),
    enabled: roomId !== undefined && Number.isFinite(roomId),
    refetchInterval: 60_000,
  })
}

// ---------------------------------------------------------------------------
// Брони
// ---------------------------------------------------------------------------

// GET /bookings отдаёт объект {server_time, meetings}, а не массив. Разворачиваем
// его здесь, в одном месте: экрану нужен список, а приведение типа в request<T>
// ничего не проверяет во время работы — из-за этого экран «Встречи» падал на
// data.map, потому что data была объектом.
export function useBookings(scope: 'upcoming' | 'past', role: 'any' | 'organizer' | 'participant') {
  return useQuery<BookingDetails[]>({
    queryKey: keys.bookings(scope, role),
    queryFn: async () => {
      const data = await request<MeetingsResponse>('/bookings', { query: { scope, role } })
      return data.meetings ?? []
    },
  })
}

export function useBooking(bookingId: number | undefined) {
  return useQuery<BookingDetails>({
    queryKey: keys.booking(bookingId ?? 0),
    queryFn: () => request<BookingDetails>(`/bookings/${bookingId}`),
    enabled: bookingId !== undefined && Number.isFinite(bookingId),
  })
}

// Создание отвечает не просто карточкой: рядом приходят warning (людей больше, чем
// мест) и skipped (даты серии, пропущенные как занятые). Тип обязан их называть,
// иначе экран «Готово» молча промолчит о том, что часть встреч не создалась.
export function useCreateBooking() {
  const invalidate = useScheduleInvalidation()
  return useMutation({
    mutationFn: (input: BookingCreate) =>
      request<BookingCreated>('/bookings', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })
}

export function useUpdateBooking() {
  const invalidate = useScheduleInvalidation()
  return useMutation({
    mutationFn: (input: {
      id: number
      room_id?: number
      title?: string
      starts_at?: string
      ends_at?: string
      apply?: 'single' | 'series'
    }) => {
      const { id, ...body } = input
      return request<BookingDetails>(`/bookings/${id}`, { method: 'PATCH', body })
    },
    onSuccess: invalidate,
  })
}

export function useCancelBooking() {
  const invalidate = useScheduleInvalidation()
  return useMutation({
    mutationFn: (input: { id: number; reason?: string; apply?: 'single' | 'series' }) =>
      request<void>(`/bookings/${input.id}`, {
        method: 'DELETE',
        query: { apply: input.apply },
        body: input.reason ? { reason: input.reason } : {},
      }),
    onSuccess: invalidate,
  })
}

export function useExtendBooking() {
  const invalidate = useScheduleInvalidation()
  return useMutation({
    mutationFn: (input: { id: number; minutes: 30 | 60 }) =>
      request<BookingDetails>(`/bookings/${input.id}/extend`, {
        method: 'POST',
        body: { minutes: input.minutes },
      }),
    onSuccess: invalidate,
  })
}

export function useDeclineBooking() {
  const invalidate = useScheduleInvalidation()
  return useMutation({
    mutationFn: (bookingId: number) =>
      request<void>(`/bookings/${bookingId}/decline`, { method: 'POST' }),
    onSuccess: invalidate,
  })
}

export function useSaveParticipants() {
  const invalidate = useScheduleInvalidation()
  return useMutation({
    mutationFn: (input: { bookingId: number; participants: ParticipantRef[] }) =>
      request<ParticipantsResponse>(`/bookings/${input.bookingId}/participants`, {
        method: 'PUT',
        body: { participants: input.participants },
      }),
    onSuccess: invalidate,
  })
}

// ---------------------------------------------------------------------------
// Сотрудники
// ---------------------------------------------------------------------------

export function useUserSearch(query: string) {
  const trimmed = query.trim()
  return useQuery<UserSuggestion[]>({
    queryKey: keys.userSearch(trimmed),
    queryFn: () => request<UserSuggestion[]>('/users/search', { query: { q: trimmed, limit: 10 } }),
    // Меньше двух символов сервер не примет — и правильно: искать по одной букве бессмысленно.
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  })
}

// ---------------------------------------------------------------------------
// Администратор
// ---------------------------------------------------------------------------

export function useCreateRoom() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RoomWrite) => request<Room>('/admin/rooms', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rooms'] })
      void queryClient.invalidateQueries({ queryKey: ['schedule'] })
    },
  })
}

export function useUpdateRoom() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number } & RoomWrite) => {
      const { id, ...body } = input
      return request<Room>(`/admin/rooms/${id}`, { method: 'PATCH', body })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rooms'] })
      void queryClient.invalidateQueries({ queryKey: ['room'] })
      void queryClient.invalidateQueries({ queryKey: ['schedule'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-future-bookings'] })
    },
  })
}

// Ответ этого адреса — не карточки встреч, а своя узкая форма: участники приходят
// числом participants_count, полей status и can_* нет. Типизировать его как
// BookingDetails[] значило бы пообещать экрану поля, которых сервер не шлёт, —
// ровно та ошибка, из-за которой раздел «Встречи» падал в белый экран.
export function useRoomFutureBookings(roomId: number | undefined, enabled: boolean) {
  return useQuery<FutureBooking[]>({
    queryKey: keys.futureBookings(roomId ?? 0),
    queryFn: () => request<FutureBooking[]>(`/admin/rooms/${roomId}/future-bookings`),
    enabled: enabled && roomId !== undefined,
  })
}

export function usePairingCode() {
  return useMutation({
    mutationFn: (roomId: number) =>
      request<PairingCodeResponse>(`/admin/rooms/${roomId}/pairing-code`, { method: 'POST' }),
  })
}

export function useDevices() {
  return useQuery<Device[]>({
    queryKey: keys.devices,
    queryFn: () => request<Device[]>('/admin/devices'),
    // «Молчит 12 минут» имеет смысл, только если строка обновляется сама.
    refetchInterval: 30_000,
  })
}

export function useRevokeDevice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (deviceId: number) => request<void>(`/admin/devices/${deviceId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.devices })
    },
  })
}

export function usePolicies() {
  return useQuery<Policy>({
    queryKey: keys.policies,
    queryFn: () => request<Policy>('/admin/policies'),
  })
}

export function useSavePolicies() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Policy) => request<Policy>('/admin/policies', { method: 'PUT', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.policies })
      // Правила приходят и в /auth/me: формы проверяют горизонт и часы по ним.
      void queryClient.invalidateQueries({ queryKey: keys.me })
    },
  })
}

export function useAdminUsers(query: string, status?: UserStatus) {
  return useQuery<User[]>({
    queryKey: keys.adminUsers(query, status),
    queryFn: () => request<User[]>('/admin/users', { query: { q: query || undefined, status } }),
    placeholderData: (previous) => previous,
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      id: number
      is_admin?: boolean
      can_recur?: boolean
      status?: UserStatus
      future_bookings?: 'keep' | 'cancel'
      reason?: string
    }) => {
      const { id, ...body } = input
      return request<User>(`/admin/users/${id}`, { method: 'PATCH', body })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      void queryClient.invalidateQueries({ queryKey: ['schedule'] })
    },
  })
}

export function useBlocklist() {
  return useQuery<BlocklistEntry[]>({
    queryKey: keys.blocklist,
    queryFn: () => request<BlocklistEntry[]>('/admin/blocklist'),
  })
}

export function useAddToBlocklist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; reason?: string }) =>
      request<BlocklistEntry>('/admin/blocklist', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.blocklist })
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

export function useRemoveFromBlocklist() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (email: string) =>
      request<void>(`/admin/blocklist/${encodeURIComponent(email)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.blocklist })
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}
